import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { CalculationsApiController } from '../api-controllers/calculations.api-controller';
import { intervalBackoff } from 'backoff-rxjs';
import { CalculationResponse } from '../../../../../../../common/dist/angular/responses/calculation.response';
import { AlertMessageStore } from '../../core/stores/alert-message.store';
import { LoggingService } from '../../core/services/logging.service';
import { SeverityTypeEnum } from '../../core/enums/severity-type.enum';
import {
  CalculationTitleUpdateOptions,
} from '../../../../../../../common/src/options/calculations/calculation-title-update.options';
import { CalculationRunOptions } from '../../../../../../../common/src/options/calculations/calculation-run.options';
import { AlertMessageFactory } from '../../core/factories/alert-message.factory';
import {
  CalculationCreationOptions,
} from '../../../../../../../common/src/options/calculations/calculation-creation.options';
import { AlertMessageModel } from '../../core/models/alert-message.model';
import { AlertContextEnum } from '../../core/enums/alert-context.enum';
import {
  CalculationGrantAdminViewOptions,
} from '../../../../../../../common/src/options/calculations/calculation-grant-admin-view.options';

@Injectable({
  providedIn: 'root',
})
export class CalculationStore {
  private autoRefreshStore: { [id: string]: Subscription } = {};

  private store: { [id: string]: BehaviorSubject<CalculationResponse | undefined> } = {};

  private errorsStore: { [id: string]: BehaviorSubject<{ property: string, messages: string[], value: any }[]> } = {};

  public constructor(
    private readonly calculationsApiController: CalculationsApiController,
    private readonly loggingService: LoggingService,
    private readonly alertMessageStore: AlertMessageStore) {
  }

  public exists(id: string): boolean {
    return this.store.hasOwnProperty(id);
  }

  public get(id: string): Observable<CalculationResponse | undefined> {
    if (this.store.hasOwnProperty(id) === false) {
      this.store[id] = new BehaviorSubject<CalculationResponse | undefined>(undefined);
      this.refresh(id);
    }

    return this.store[id];
  }

  public getErrors(id: string): Observable<{ property: string, messages: string[], value: any }[]> {
    if (this.errorsStore.hasOwnProperty(id) === false) {
      this.errorsStore[id] = new BehaviorSubject<{ property: string, messages: string[], value: any }[]>([]);
    }

    return this.errorsStore[id];
  }

  public setErrors(id: string, validationErrors: { property: string, messages: string[], value: any }[] = []) {
    if (this.errorsStore.hasOwnProperty(id) === false) {
      this.errorsStore[id] = new BehaviorSubject<{ property: string, messages: string[], value: any }[]>([]);
    }

    this.errorsStore[id].next(validationErrors);
  }

  public async save(id: string, calculationResponse: CalculationResponse): Promise<void> {
    if (this.store.hasOwnProperty(id) === false) {
      this.store[id] = new BehaviorSubject<CalculationResponse | undefined>(calculationResponse);
    } else {
      this.store[id].next(calculationResponse);
    }
  }

  public async refresh(id: string): Promise<void> {
    this.loggingService.debug('Refreshing calculation', { id });

    try {
      const calculationResponse = await this.calculationsApiController.get(id);

      await this.save(id, calculationResponse);
    } catch (error) {
      this.loggingService.error('[calculation-store] - There was an error retrieving the Calculation', { error });

      if (error.status === 404) {
        this.remove(id);
      }

      this.alertMessageStore.saveAll(AlertMessageFactory.create(error, SeverityTypeEnum.Error));
    }
  }

  public async update(id: string, calculationRunOptions: CalculationRunOptions): Promise<void> {
    try {
      const calculationResponse = await this.calculationsApiController.update(id, calculationRunOptions);

      await this.save(id, calculationResponse);

      this.alertMessageStore.save(new AlertMessageModel(SeverityTypeEnum.Success, AlertContextEnum.Global, {
        fr: 'Le calcul a bien été sauvegardé',
        en: 'The calculation has been successfully saved.',
      }));
    } catch (error) {
      this.loggingService.error('[calculation-store] - There was an error updating the Calculation', { error });

      this.alertMessageStore.saveAll(AlertMessageFactory.create(error, SeverityTypeEnum.Error));

      throw error;
    }
  }

  public async updateTitle(id: string, calculationTitleUpdateOptions: CalculationTitleUpdateOptions): Promise<void> {
    try {
      const calculationResponse = await this.calculationsApiController.updateTitle(id, calculationTitleUpdateOptions);

      await this.save(id, calculationResponse);
    } catch (error) {
      this.loggingService.error('[calculation-store] - There was an error updating the title of the Calculation', { error });

      this.alertMessageStore.saveAll(AlertMessageFactory.create(error, SeverityTypeEnum.Error));

      throw error;
    }
  }


  public async grantDelegatedAccess(id: string, options: CalculationGrantAdminViewOptions): Promise<void> {
    try {
      const calculationResponse = await this.calculationsApiController.grantDelegatedAccess(id, options);

      await this.save(id, calculationResponse);
    } catch (error) {
      this.loggingService.error('[calculation-store] - There was an error granting delegated access to the Calculation', { error });

      this.alertMessageStore.saveAll(AlertMessageFactory.create(error, SeverityTypeEnum.Error));

      throw error;
    }
  }

  public async create(calculationCreationOptions: CalculationCreationOptions): Promise<Observable<CalculationResponse>> {
    try {
      const calculationResponse = await this.calculationsApiController.create(calculationCreationOptions);

      await this.save(calculationResponse.id, calculationResponse);

      return this.store[calculationResponse.id];
    } catch (error) {
      this.loggingService.error('[calculation-store] - There was an error creating the Calculation', { error });

      this.alertMessageStore.saveAll(AlertMessageFactory.create(error, SeverityTypeEnum.Error));

      throw error;
    }
  }

  public async run(id: string, calculationRunOptions?: CalculationRunOptions): Promise<void> {
    try {
      const calculationResponse = await this.calculationsApiController.run(id, calculationRunOptions);

      await this.save(id, calculationResponse);
    } catch (error) {
      this.loggingService.error('[calculation-store] - There was an error running the Calculation', { error });

      this.alertMessageStore.saveAll(AlertMessageFactory.create(error, SeverityTypeEnum.Error));

      throw error;
    }
  }

  public async delete(id: string): Promise<void> {
    try {
      await this.calculationsApiController.delete(id);

      this.remove(id);
      this.alertMessageStore.save(new AlertMessageModel(SeverityTypeEnum.Success, AlertContextEnum.CalculationDeleted, {
        fr: 'Le calcul a bien été suprrimé',
        en: 'The calculation has been successfully deleted.',
      }));
    } catch (error) {
      this.loggingService.error('[calculation-store] - There was an error deleting the Calculation', { error });

      this.alertMessageStore.saveAll(AlertMessageFactory.create(error, SeverityTypeEnum.Error));

      throw error;
    }
  }

  public startAutoRefresh(calculationId: string): void {
    if (this.autoRefreshStore[calculationId]) {
      return;
    }
    this.autoRefreshStore[calculationId] = intervalBackoff({
      initialInterval: 200,
      maxInterval: 1500,
    }).subscribe(async () => {
      await this.refresh(calculationId);
    });
  }

  public stopAutoRefresh(calculationId: string): void {
    if (!this.autoRefreshStore[calculationId]) {
      return;
    }
    this.autoRefreshStore[calculationId].unsubscribe();
    this.autoRefreshStore[calculationId] = undefined;
  }

  public remove(calculationId: string): void {
    this.store[calculationId].complete();
    delete this.store[calculationId];
  }
}
